// Centrální registr produktů — strom kontext (detail stanice / skupina stanic) → kategorie →
// produkt (list). Jeden zdroj pravdy, ze kterého se odvozují projekce používané v layoutech
// (REPORT_MAP, MENU_ITEMS, GROUP_PRODUCTS apod.), viz doc/plans/product-registry-refactor.md,
// etapa 1. Dnešní REPORT_MAP/MENU_ITEMS (StationDetailLayout.jsx) a GROUP_PRODUCTS/
// GROUP_PERIOD_PRODUCTS (measConfig.js/groupPeriodConfig.js) se tímto souborem nahrazují jako
// ručně psané duplicitní struktury — zůstávají jen jako odvozené exporty níže.
//
// Tvar uzlu:
//   list (produkt):     { slug, label, component }
//   kategorie:          { key, label, chips, items: [list, ...] }
//   kořen (kontext):     { key, categories: [kategorie, ...] }
//
// `denyInVariants`/role pole (etapa 2/3) se do uzlů doplní až v příslušné etapě — v etapě 1
// registr žádný takový filtr neřeší.

import { HlyOneMonthTempView } from '../components/reports/HlyOneMonthTempView'
import { HlyOneMonthPrecipView } from '../components/reports/HlyOneMonthPrecipView'
import { MinOneDayView } from '../components/reports/MinOneDayView'
import { HlyOneDayView } from '../components/reports/HlyOneDayView'
import { HlyGrafView } from '../components/reports/HlyGrafView'
import { DlyGrafView } from '../components/reports/DlyGrafView'
import { DlyOneYearTempView } from '../components/reports/DlyOneYearTempView'
import { DlyOneYearPrecipView } from '../components/reports/DlyOneYearPrecipView'
import { DlyOneYearSnowView } from '../components/reports/DlyOneYearSnowView'
import { DlyOneYearSunView } from '../components/reports/DlyOneYearSunView'
import { DlyOneYearWindView } from '../components/reports/DlyOneYearWindView'
import { DlyOneYearHumidView } from '../components/reports/DlyOneYearHumidView'
import { DlyOneYearSoilView } from '../components/reports/DlyOneYearSoilView'
import { DlyOneMonthView } from '../components/reports/DlyOneMonthView'
import { MlyPrehledView } from '../components/reports/MlyPrehledView'
import { MlyTeplotyView } from '../components/reports/MlyTeplotyView'
import { MlySrazkyView } from '../components/reports/MlySrazkyView'
import { MlySvitView } from '../components/reports/MlySvitView'
import { MlyVitrView } from '../components/reports/MlyVitrView'
import { MlyVlhkostView } from '../components/reports/MlyVlhkostView'
import { MlyPudniView } from '../components/reports/MlyPudniView'
import { YlyPrehledView } from '../components/reports/YlyPrehledView'
import { YlyTeplotyView } from '../components/reports/YlyTeplotyView'
import { YlySrazkyView } from '../components/reports/YlySrazkyView'
import { MlyVitrRuziceView } from '../components/reports/MlyVitrRuziceView'
import { YlyVitrRuziceView } from '../components/reports/YlyVitrRuziceView'
import { DlyJevyView } from '../components/reports/DlyJevyView'
import { GrafView } from '../components/reports/GrafView'
import { PeriodGrafView } from '../components/reports/PeriodGrafView'
import { DalsiGrafView } from '../components/reports/DalsiGrafView'
import { StationGroupMeasView } from '../components/group/StationGroupMeasView'
import { StationGroupPeriodView } from '../components/group/StationGroupPeriodView'
import { StationGroupJevyView } from '../components/group/StationGroupJevyView'

// ---------------------------------------------------------------------------
// Kontext "Detail stanice" (dnešní REPORT_MAP + MENU_ITEMS ve StationDetailLayout.jsx)
// ---------------------------------------------------------------------------

const STATION_DETAIL_CATEGORIES = [
  {
    key: '10minutova-data', label: '10minutová data', chips: true,
    items: [
      { slug: 'min-prehled', label: 'Přehled', component: MinOneDayView },
      { slug: 'graf', label: 'Přehled-Graf', component: GrafView },
    ],
  },
  {
    key: 'hodinova-data', label: 'Hodinová data', chips: true,
    items: [
      { slug: 'hly-prehled', label: 'Přehled', component: HlyOneDayView },
      { slug: 'hly-graf', label: 'Přehled-Graf', component: HlyGrafView },
      { slug: 'hly-temp', label: 'Teploty', component: HlyOneMonthTempView },
      { slug: 'hly-precip', label: 'Srážky', component: HlyOneMonthPrecipView },
    ],
  },
  {
    key: 'denni-data', label: 'Denní data', chips: true,
    items: [
      { slug: 'dly-prehled', label: 'Přehled', component: DlyOneMonthView },
      { slug: 'dly-graf', label: 'Přehled-Graf', component: DlyGrafView },
      { slug: 'dly-one-year-temp', label: 'Teploty', component: DlyOneYearTempView },
      { slug: 'dly-one-year-precip', label: 'Srážky', component: DlyOneYearPrecipView },
      { slug: 'dly-one-year-snow', label: 'Sníh', component: DlyOneYearSnowView },
      { slug: 'dly-one-year-sun', label: 'Svit', component: DlyOneYearSunView },
      { slug: 'dly-one-year-wind', label: 'Vítr', component: DlyOneYearWindView },
      { slug: 'dly-one-year-humid', label: 'Vlhkost', component: DlyOneYearHumidView },
      { slug: 'dly-one-year-soil', label: 'Půdní teploty', component: DlyOneYearSoilView },
    ],
  },
  {
    key: 'mesicni-data', label: 'Měsíční data', chips: true,
    items: [
      { slug: 'mly-prehled', label: 'Přehled', component: MlyPrehledView },
      { slug: 'mly-teploty', label: 'Teploty', component: MlyTeplotyView },
      { slug: 'mly-srazky', label: 'Srážky & sníh', component: MlySrazkyView },
      { slug: 'mly-svit', label: 'Svit & záření', component: MlySvitView },
      { slug: 'mly-vitr', label: 'Vítr & tlak', component: MlyVitrView },
      { slug: 'mly-vlhkost', label: 'Vlhkost', component: MlyVlhkostView },
      { slug: 'mly-pudni', label: 'Půdní teploty', component: MlyPudniView },
      { slug: 'mly-vitr-ruzice', label: 'Větrná růžice', component: MlyVitrRuziceView },
    ],
  },
  {
    key: 'rocni-data', label: 'Roční data', chips: true,
    items: [
      { slug: 'yly-prehled', label: 'Přehled', component: YlyPrehledView },
      { slug: 'yly-teploty', label: 'Teploty & dny', component: YlyTeplotyView },
      { slug: 'yly-srazky', label: 'Srážky & klima', component: YlySrazkyView },
      { slug: 'yly-vitr-ruzice', label: 'Větrná růžice', component: YlyVitrRuziceView },
    ],
  },
  {
    key: 'mesicni-rocni-grafy', label: 'Měsíční/Roční grafy', chips: true,
    items: [
      { slug: 'mly-yly-prehled', label: 'Teploty a srážky', component: ({ station }) => <PeriodGrafView station={station} chartType="ts_month" slug="mly-yly-prehled" /> },
      { slug: 'mly-yly-teploty', label: 'Teploty', component: ({ station }) => <PeriodGrafView station={station} chartType="dmdatat" slug="mly-yly-teploty" /> },
      { slug: 'mly-yly-tepavg', label: 'Prům. teplota', component: ({ station }) => <PeriodGrafView station={station} chartType="dmdatatep" slug="mly-yly-tepavg" /> },
      { slug: 'mly-yly-srazky', label: 'Srážky', component: ({ station }) => <PeriodGrafView station={station} chartType="dmdatasra" slug="mly-yly-srazky" /> },
      { slug: 'mly-yly-snih', label: 'Nový sníh', component: ({ station }) => <PeriodGrafView station={station} chartType="dmdatasno" slug="mly-yly-snih" /> },
      { slug: 'mly-yly-snihvys', label: 'Výška sněhu', component: ({ station }) => <PeriodGrafView station={station} chartType="dmdatasce" slug="mly-yly-snihvys" /> },
      { slug: 'mly-yly-svit', label: 'Sluneční svit', component: ({ station }) => <PeriodGrafView station={station} chartType="dmdatassv" slug="mly-yly-svit" /> },
    ],
  },
  {
    key: 'jevy', label: 'Jevy', chips: true,
    items: [
      { slug: 'dly-jevy', label: 'Jevy', component: DlyJevyView },
    ],
  },
  {
    key: 'dalsi-grafy', label: 'Další grafy', chips: true,
    items: [
      { slug: 'dalsi-tsranorm', label: 'Srážky a teploty', component: ({ station }) => <DalsiGrafView station={station} chartType="tsranorm" slug="dalsi-tsranorm" /> },
      { slug: 'dalsi-sranorm', label: 'Kumulativní srážky', component: ({ station }) => <DalsiGrafView station={station} chartType="sranorm" slug="dalsi-sranorm" /> },
    ],
  },
]

// ---------------------------------------------------------------------------
// Kontext "Skupina stanic" (dnešní GROUP_PRODUCTS/GROUP_PERIOD_PRODUCTS v
// measConfig.js/groupPeriodConfig.js) — measGranularities/period produkty jsou definované
// v samostatných souborech (MEAS_GRANULARITIES, GROUP_MLY_PRODUCTS, GROUP_YLY_PRODUCTS),
// protože nesou i data mimo menu (tsId, jednotky, sloupce pro tabulky/grafy) — sem se
// přebírá jen projekce label/slug/component potřebná pro menu a routing.
// ---------------------------------------------------------------------------

import { MEAS_GRANULARITIES } from './measConfig'
import { GROUP_MLY_PRODUCTS, GROUP_YLY_PRODUCTS } from './groupPeriodConfig'

const measCategory = (gran) => ({
  key: `meas-${gran.key}`,
  label: gran.label,
  chips: true,
  items: gran.items.map(item => ({
    slug: `meas-${item.q}`,
    label: item.label,
    component: ({ selectedNode, selectedPath, stationsByGhId, filteredTree }) => (
      <StationGroupMeasView
        measQ={item.q}
        selectedNode={selectedNode}
        selectedPath={selectedPath}
        stationsByGhId={stationsByGhId}
        filteredTree={filteredTree}
      />
    ),
  })),
})

const STATION_GROUP_CATEGORIES = [
  ...MEAS_GRANULARITIES.map(measCategory),
  {
    key: 'grp-mly', label: 'Měsíční data – skupina', chips: true,
    items: GROUP_MLY_PRODUCTS.map(p => ({
      slug: `grp-mly-${p.key}`,
      label: p.label,
      component: (props) => <StationGroupPeriodView kind="mly" productSlug={`grp-mly-${p.key}`} {...props} />,
    })),
  },
  {
    key: 'grp-yly', label: 'Roční data – skupina', chips: true,
    items: GROUP_YLY_PRODUCTS.map(p => ({
      slug: `grp-yly-${p.key}`,
      label: p.label,
      component: (props) => <StationGroupPeriodView kind="yly" productSlug={`grp-yly-${p.key}`} {...props} />,
    })),
  },
  {
    key: 'grp-jevy', label: 'Jevy – skupina', chips: true,
    items: [
      { slug: 'grp-jevy', label: 'Jevy', component: (props) => <StationGroupJevyView {...props} /> },
    ],
  },
]

// ---------------------------------------------------------------------------
// Strom
// ---------------------------------------------------------------------------

export const PRODUCT_REGISTRY = [
  { key: 'station-detail', categories: STATION_DETAIL_CATEGORIES },
  { key: 'station-group', categories: STATION_GROUP_CATEGORIES },
]

const findRoot = (rootKey) => PRODUCT_REGISTRY.find(r => r.key === rootKey)

// ---------------------------------------------------------------------------
// Odvozené projekce — dnešní tvar dat pro layouty (beze změny API pro JSX, které je renderuje)
// ---------------------------------------------------------------------------

// Ekvivalent REPORT_MAP (StationDetailLayout.jsx) — slug → komponenta.
export const STATION_DETAIL_REPORT_MAP = Object.fromEntries(
  STATION_DETAIL_CATEGORIES.flatMap(c => c.items.map(item => [item.slug, item.component]))
)

// Ekvivalent MENU_ITEMS (StationDetailLayout.jsx) — pole kategorií v dnešním tvaru
// { group, chips, items: [{ label, slug }] }.
export const STATION_DETAIL_MENU_ITEMS = STATION_DETAIL_CATEGORIES.map(c => ({
  group: c.label,
  chips: c.chips,
  items: c.items.map(({ slug, label }) => ({ slug, label })),
}))

// Ekvivalent GROUP_PRODUCTS (measConfig.js) — pole kategorií v dnešním tvaru
// { group, chips, items: [{ label, slug }] }.
export const GROUP_PRODUCTS = STATION_GROUP_CATEGORIES.map(c => ({
  group: c.label,
  chips: c.chips,
  items: c.items.map(({ slug, label }) => ({ slug, label })),
}))

// Vyhledání listu (produktu) podle slugu v dané větvi (kontextu) — nahrazuje routing podle
// prefixu slugu (startsWith('meas-')/'grp-mly-'/... ve StationGroupLayout.jsx).
export function findProductNode(rootKey, slug) {
  const root = findRoot(rootKey)
  if (!root || !slug) return null
  for (const category of root.categories) {
    const item = category.items.find(i => i.slug === slug)
    if (item) return item
  }
  return null
}

// Projekce slug → { context, category, label } pro sledování přístupů (viz
// doc/plans/usage-analytics-client.md sekce 2.3). Díky ní stačí volajícímu předat
// jen slug — kontext a kategorii, které jsou vlastností registru, si hook dohledá sám.
// `context` je klíč kořene (station-detail / station-group), `category` klíč kategorie.
export const PRODUCT_BY_SLUG = Object.fromEntries(
  PRODUCT_REGISTRY.flatMap(root =>
    root.categories.flatMap(category =>
      category.items.map(item => [
        item.slug,
        { context: root.key, category: category.key, label: item.label },
      ])
    )
  )
)
